import React, { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useRemirror } from '@remirror/react';

import Switch from 'components/Switch/Switch';

import { cn } from '../lib/utils';
import { UILabel } from '../ui';

import { MarkdownTextEditor } from './Modes/Markdown';
import { VisualEditor } from './Modes/Visual';
import {
  markdownModeExtensions,
  visualModeExtensions,
  WrapperEditor
} from './utils';

import '@remirror/styles/all.css';
import './styles.css';

enum EditorMode {
  Visual = 'visual',
  Markdown = 'markdown'
}

interface IDualEditorProps {
  defaultMode?: EditorMode;
  allowSwitching?: boolean;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  isRequired?: boolean;
  'data-testid'?: string;
  'data-testid-header'?: string;
  'data-testid-editor'?: string;
}

export const MarkdownEditorWrapper: React.FC<IDualEditorProps> = ({
  defaultMode = EditorMode.Visual,
  allowSwitching = false,
  value,
  onChange,
  placeholder = 'Write something...',
  label = '',
  disabled = false,
  isRequired = false,
  'data-testid': dataTestId
}) => {
  const [isVisualMode, setIsVisualMode] = useState<boolean>(
    defaultMode === EditorMode.Visual
  );

  const visual = useRemirror({
    extensions: visualModeExtensions,
    stringHandler: 'markdown',
    content: value
  });
  const markdown = useRemirror({
    extensions: markdownModeExtensions,
    builtin: {
      exitMarksOnArrowPress: false
    },
    stringHandler: 'html'
  });

  useEffect(() => {
    if (!markdown || !visual) return;

    markdown.getContext()?.setContent({
      type: 'doc',
      content: [
        {
          type: 'codeBlock',
          attrs: { language: 'markdown' },
          content: value ? [{ type: 'text', text: value }] : undefined
        }
      ]
    });

    visual.getContext()?.setContent(value || '');
  }, [value, markdown, visual]);

  return (
    <>
      {label && (
        <div
          data-testid={`${dataTestId}-Header`}
          className={cn(
            'flex text-p-s mb-2 justify-between items-center',
            disabled ? 'text-base-mutedForeground' : 'text-base-foreground'
          )}
        >
          <UILabel htmlFor={'uiInput'} className="flex text-p-s">
            {isRequired && (
              <div className="mr-2 mt-1">
                <FontAwesomeIcon
                  icon="fa-regular fa-asterisk"
                  className="h-2 w-2 text-[8px]"
                />
              </div>
            )}
            {label}
          </UILabel>
          {allowSwitching && (
            <div className="flex items-center space-x-2">
              <UILabel htmlFor="editor-mode">Dev Mode</UILabel>
              <Switch
                id="editor-mode"
                textAlignment="left"
                onCheckedChange={() => setIsVisualMode((prev) => !prev)}
              />
            </div>
          )}
        </div>
      )}
      <WrapperEditor visual={visual} markdown={markdown}>
        <div className={isVisualMode ? 'block' : 'hidden'}>
          <VisualEditor onChange={onChange} placeholder={placeholder} />
        </div>
        <div
          className={!isVisualMode ? 'block' : 'hidden'}
          data-testid={`${dataTestId}-Editor`}
        >
          <MarkdownTextEditor onChange={onChange} placeholder={placeholder} />
        </div>
      </WrapperEditor>
    </>
  );
};
